/**
 * Custom Featured Products Section
 * Handles cart interactions and dynamic updates
 */

(function () {
  'use strict';

  class CustomFeaturedProducts {
    constructor() {
      this.selectors = {
        form: '.custom-featured-form',
        addToCartBtn: '.custom-featured-add-to-cart',
        cart: '[data-cart]',
        cartCount: '[data-cart-count]',
        productItem: '.custom-featured-item',
        bgImageElements: '[data-bg-image]',
        clickableCards: '.custom-featured-side-card, .custom-featured-right-column, .custom-featured-hero',
      };

      this.classes = {
        loading: 'is-loading',
        success: 'is-success',
        error: 'is-error',
      };

      this.init();
    }

    init() {
      this.setBackgroundImages();
      this.bindEvents();
      this.setupIntersectionObserver();
    }

    setBackgroundImages() {
      // Set background images from data attributes
      const elements = document.querySelectorAll(this.selectors.bgImageElements);
      elements.forEach((el) => {
        const bgImage = el.getAttribute('data-bg-image');
        if (bgImage) {
          el.style.setProperty('--bg-image', `url(${bgImage})`);
          el.style.setProperty('--hero-bg-image', `url(${bgImage})`);
        }
      });
    }

    bindEvents() {
      document.addEventListener('submit', (e) => {
        if (e.target.classList.contains('custom-featured-form')) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          this.handleAddToCart(e);
        }
      }, true);

      document.addEventListener('click', (e) => {
        const card = e.target.closest(this.selectors.clickableCards);
        if (!card) return;

        if (e.target.closest('a, button, input, select, textarea, label, form')) return;

        const url = card.getAttribute('data-url');
        if (url) {
          window.location.href = url;
        }
      });
    }

    handleAddToCart(e) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      const form = e.target;
      const btn = form.querySelector('button[type="submit"]');

      if (btn && btn.classList.contains(this.classes.loading)) {
        return;
      }
      
      // Get variant ID from form
      let variantId = null;
      const idInput = form.querySelector('input[name="id"]');
      
      if (idInput && idInput.value) {
        variantId = idInput.value;
      } else {
        // Try alternate selectors
        const variantInput = form.querySelector('[name="id"]');
        if (variantInput && variantInput.value) {
          variantId = variantInput.value;
        }
      }

      if (!variantId) {
        console.error('Could not find variant ID in form', form);
        this.showNotification('Please select a product variant.', 'error');
        return;
      }

      // Show loading state
      this.setButtonLoading(btn, true);

      fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          items: [{
            id: variantId,
            quantity: 1
          }]
        }),
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          return response.json();
        })
        .then((data) => {
          // Reset button state
          this.setButtonLoading(btn, false);

          // Update cart count immediately
          this.updateCartCount();

          // Dispatch ajaxProduct:added event - this triggers cart.js to refresh
          // the cart items and open the drawer automatically
          const event = new CustomEvent('ajaxProduct:added', {
            bubbles: true,
            detail: { 
              source: 'custom-featured-products',
              product: data
            }
          });
          document.dispatchEvent(event);

          // Trigger cart refresh event for theme compatibility
          document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
            bubbles: true
          }));
        })
        .catch((error) => {
          console.error('Error adding to cart:', error);
          this.showNotification(
            'Failed to add product. Please try again.',
            'error'
          );
          this.setButtonLoading(btn, false);
        });
    }

    setButtonLoading(btn, isLoading) {
      if (!btn) return;
      
      if (isLoading) {
        btn.classList.add(this.classes.loading);
        btn.disabled = true;
        btn.setAttribute('aria-busy', 'true');
        btn.setAttribute('data-loading', 'true');
        btn.style.opacity = '0.6';
        btn.style.pointerEvents = 'none';
      } else {
        btn.classList.remove(this.classes.loading);
        btn.disabled = false;
        btn.removeAttribute('aria-busy');
        btn.removeAttribute('data-loading');
        btn.style.opacity = '1';
        btn.style.pointerEvents = 'auto';
      }
    }

    openCartDrawer() {
      // Use Woolman ModalsAndDrawers system (Shinergy theme standard)
      if (typeof Woolman !== 'undefined' && Woolman.ModalsAndDrawers && typeof Woolman.ModalsAndDrawers.showModalOrDrawer === 'function') {
        Woolman.ModalsAndDrawers.showModalOrDrawer('drawer-cart');
      } else {
        // Fallback methods for compatibility with other themes
        
        // Method 1: Dispatch custom event
        document.dispatchEvent(new CustomEvent('cart:open', {
          bubbles: true,
          detail: { source: 'custom-featured-products' }
        }));

        // Method 2: Look for cart drawer element and trigger it
        const cartDrawer = document.querySelector('[id*="cart"]');
        if (cartDrawer) {
          // Try to open if it's a details/dialog element
          if (cartDrawer.tagName === 'DETAILS') {
            cartDrawer.open = true;
          } else if (cartDrawer.tagName === 'DIALOG') {
            if (typeof cartDrawer.showModal === 'function') {
              cartDrawer.showModal();
            }
          }
        }

        // Method 3: Look for cart button/trigger and click it
        const cartTrigger = document.querySelector('[aria-label*="cart"], [aria-label*="Cart"], [data-cart-drawer-trigger], .cart-icon-bubble');
        if (cartTrigger) {
          cartTrigger.click();
        }

        // Method 4: Check for window-level cart function
        if (typeof window.openCart === 'function') {
          window.openCart();
        } else if (typeof window.cartDrawer !== 'undefined' && window.cartDrawer.open) {
          window.cartDrawer.open();
        }
      }
    }

    updateCartCount() {
      fetch('/cart.js')
        .then((response) => response.json())
        .then((cart) => {
          const cartCountElements = document.querySelectorAll(
            this.selectors.cartCount
          );
          if (cartCountElements.length > 0) {
            cartCountElements.forEach((el) => {
              el.textContent = cart.item_count;
            });
          }
          
          // Also update any cart badge elements
          const badges = document.querySelectorAll('[data-cart-count-bubble]');
          badges.forEach(badge => {
            badge.textContent = cart.item_count;
            if (cart.item_count > 0) {
              badge.classList.remove('hidden');
            }
          });
        })
        .catch((error) => console.error('Error updating cart count:', error));
    }

    showNotification(message, type = 'info') {
      // Create notification element
      const notification = document.createElement('div');
      notification.className = `custom-featured-notification notification-${type}`;
      notification.innerHTML = `
        <div class="notification-content">
          <span class="notification-message">${message}</span>
          <button class="notification-close" aria-label="Close notification">
            ×
          </button>
        </div>
      `;

      document.body.appendChild(notification);

      // Add styles if not already present
      if (!document.getElementById('custom-featured-notification-styles')) {
        const styleEl = document.createElement('style');
        styleEl.id = 'custom-featured-notification-styles';
        styleEl.textContent = `
          .custom-featured-notification {
            position: fixed;
            top: 20px;
            right: 20px;
            max-width: 400px;
            padding: 16px;
            background: white;
            border-radius: 8px;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
            z-index: 9999;
            animation: slideIn 0.3s ease-out;
          }

          @keyframes slideIn {
            from {
              transform: translateX(420px);
              opacity: 0;
            }
            to {
              transform: translateX(0);
              opacity: 1;
            }
          }

          .custom-featured-notification.notification-success {
            border-left: 4px solid #70d44b;
          }

          .custom-featured-notification.notification-error {
            border-left: 4px solid #e74c3c;
          }

          .custom-featured-notification.notification-info {
            border-left: 4px solid #3498db;
          }

          .notification-content {
            display: flex;
            justify-content: space-between;
            align-items: center;
            gap: 12px;
          }

          .notification-message {
            font-size: 14px;
            color: #333;
            flex: 1;
          }

          .notification-close {
            background: none;
            border: none;
            font-size: 24px;
            color: #999;
            cursor: pointer;
            padding: 0;
            width: 24px;
            height: 24px;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: color 0.2s ease;
          }

          .notification-close:hover {
            color: #333;
          }

          @media (max-width: 640px) {
            .custom-featured-notification {
              right: 10px;
              left: 10px;
              max-width: none;
            }
          }
        `;
        document.head.appendChild(styleEl);
      }

      // Handle close button
      const closeBtn = notification.querySelector('.notification-close');
      closeBtn.addEventListener('click', () => {
        notification.style.animation = 'slideOut 0.3s ease-out';
        setTimeout(() => notification.remove(), 300);
      });

      // Auto remove after 5 seconds
      setTimeout(() => {
        if (notification.parentElement) {
          notification.style.animation = 'slideOut 0.3s ease-out';
          setTimeout(() => notification.remove(), 300);
        }
      }, 5000);
    }

    setupIntersectionObserver() {
      // Lazy load images when they come into view
      const images = document.querySelectorAll('.custom-featured-image');

      if ('IntersectionObserver' in window) {
        const imageObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              const img = entry.target;
              if (img.dataset.src) {
                img.src = img.dataset.src;
                img.removeAttribute('data-src');
              }
              imageObserver.unobserve(img);
            }
          });
        });

        images.forEach((img) => imageObserver.observe(img));
      }
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      new CustomFeaturedProducts();
    });
  } else {
    new CustomFeaturedProducts();
  }
})();
